/**
 * Date and Time Utility Functions
 * 
 * This module provides utility functions for formatting dates and times
 * from ISO strings, specifically handling UTC timezone to prevent shifts.
 */

/**
 * Formats an ISO date string into separate date and time components
 * @param {string} isoString - ISO date string (e.g., "2024-12-25T10:30:00.000Z")
 * @returns {Object} Object containing formattedDate and formattedTime
 * @example
 * const { formattedDate, formattedTime } = formatDateTime("2024-12-25T10:30:00.000Z");
 * // formattedDate: "Dec 25, 2024"
 * // formattedTime: "10:30 AM"
 */
export const formatDateTime = (isoString) => {
  // Parse the ISO string to a Date object
  // The backend sends dates in UTC (+00:00)
  const date = new Date(isoString);

  // Format date only
  const formattedDate = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC", // Explicitly use UTC to prevent timezone shifts
  }).format(date);

  // Format time only
  const formattedTime = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC", // Explicitly use UTC to prevent timezone shifts
  }).format(date);

  return { formattedDate, formattedTime };
};

/**
 * Formats an ISO date string into a combined date and time string
 * @param {string} isoString - ISO date string (e.g., "2024-12-25T10:30:00.000Z")
 * @returns {string} Combined formatted date and time string
 * @example
 * const combined = formatDateTimeCombined("2024-12-25T10:30:00.000Z");
 * // Returns: "25 Dec, 2024 10:30 AM"
 */
export const formatDateTimeCombined = (isoString) => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  }).format(date);
};

/**
 * Formats an ISO date string to just the date part
 * @param {string} isoString - ISO date string
 * @returns {string} Formatted date string
 * @example
 * const dateOnly = formatDateOnly("2024-12-25T10:30:00.000Z");
 * // Returns: "Dec 25, 2024"
 */
export const formatDateOnly = (isoString) => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

/**
 * Formats an ISO date string to just the time part
 * @param {string} isoString - ISO date string
 * @returns {string} Formatted time string
 * @example
 * const timeOnly = formatTimeOnly("2024-12-25T10:30:00.000Z");
 * // Returns: "10:30 AM"
 */
export const formatTimeOnly = (isoString) => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  }).format(date);
};

/**
 * Formats an ISO date string for local timezone display
 * @param {string} isoString - ISO date string
 * @returns {Object} Object containing formattedDate and formattedTime in local timezone
 * @example
 * const { formattedDate, formattedTime } = formatDateTimeLocal("2024-12-25T10:30:00.000Z");
 * // Returns date/time converted to user's local timezone
 */
export const formatDateTimeLocal = (isoString) => {
  const date = new Date(isoString);

  const formattedDate = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);

  const formattedTime = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  return { formattedDate, formattedTime };
};

/**
 * Gets relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string} isoString - ISO date string
 * @returns {string} Relative time string
 * @example
 * const relativeTime = getRelativeTime("2024-12-25T10:30:00.000Z");
 * // Returns: "2 hours ago" or "3 days ago", etc.
 */
export const getRelativeTime = (isoString) => {
  const date = new Date(isoString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return "Just now";
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  } else {
    return formatDateOnly(isoString);
  }
};

// Default export for convenience
export default {
  formatDateTime,
  formatDateTimeCombined,
  formatDateOnly,
  formatTimeOnly,
  formatDateTimeLocal,
  getRelativeTime,
};